/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useEffect, useState } from "react";

// @material-ui/core components
import { makeStyles } from "@material-ui/core/styles";
import {
  Button,
  Box,
  Container,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Divider,
} from "@material-ui/core";

// @material-ui/icons components
import HeaderClientes from "components/_refactor/headers/clients";
import useSnackbar from "components/Alerts/Snackbar/useSnackbar";
import StructureFields from "components/_refactor/fields/structure";
import DialogManutencaoInfoComplementar from "components/Dialogs/DialogManutencaoInfoComplementar";
import Api from "lib/api/httpRequestHelper";
import UtilsFncs from "utils/UtilsFncs";
import componentStyles from "assets/theme/components/cards/sortable/card-light-table-sortable";
import ClearButton from "components/_refactor/button/clear";
import LoadingData from "components/Loading/LoadingData";
import TableInfosComplementares from "./table/TableInfosComplementares";
import useLogs from "hooks/logs";
import { IsMobile900, IsMobile } from "utils/is-mobile";

const requiredFields = ["brand", "representative"];

const useStyles = makeStyles(componentStyles);

function ManutencaoInformacoesComplementares() {
  const classes = useStyles();
  const snackbar = useSnackbar();
  const { setLocalStorageActions, setLocalStorageApiProblem } = useLogs();

  const [infos, setInfos] = useState(null);
  const [selectedStore, setSelectedStore] = useState(null);
  const [searching, setSearching] = useState(false);
  const [structureFilters, setStructureFilters] = useState({});
  const [structureLoading, setStructureLoading] = useState([]);
  const [hasUpdated, setHasUpdated] = useState(false);
  const [clearFilters, setClearFilters] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [errorInfo, setErrorInfo] = useState(null);
  const [firstSearch, setFirstSearch] = useState(false);
  const [hasNoBrand, setHasNoBrand] = useState(false);
  const [hasNoRepresentative, setHasNoRepresentative] = useState(false);
  const [totalRegisters, setTotalRegisters] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [currentSizePerPage, setCurrentSizePerPage] = useState(25);

  const isMobile900 = IsMobile900();
  const isMobile = IsMobile();

  const updateList = useCallback(
    async (lojaInfoComplementar, updatedMenu) => {
      const newMenuList = infos?.map((loja) => {
        if (loja.IdLoja === updatedMenu.IdLoja)
          return { ...loja, ...updatedMenu };
        else return loja;
      });
      setInfos(newMenuList);
      setHasUpdated(true);
    },
    [infos]
  );

  const fetchList = async ({ page = 1, sizePerPage = 25 }) => {
    setSearching(true);
    setHasUpdated(false);
    setCancelLoading(false);
    setErrorInfo(null);

    return await Api.get(
      "list-complementary-info",
      "storeadditionalinfo",
      UtilsFncs.RestParamsComposer(structureFilters) +
        `&page=${page - 1}` +
        `&pageSize=${sizePerPage ? sizePerPage : 25}`,
      true
    )
      .then(({ Success, Result }) => {
        if (Success) {
          setInfos(Result?.Lojas || []);
          setTotalRegisters(Result?.TotalRegistro);
          setCurrentPage(page);
          setCurrentSizePerPage(sizePerPage);
        }
      })
      .catch((error) => {
        setErrorInfo(error);
        setLocalStorageApiProblem({
          idMenuAccess: "131",
          screen: "Manutenção de Informações Complementares de Loja",
          problem: `Erro na chamada do endpoint list-complementary-info - ${error}`,
        });
      })
      .finally(() => setSearching(false));
  };

  const handleClearFilters = () => {
    setClearFilters(true);
    setSearching(true);
    setStructureLoading(true);
    setSearching(false);
    setStructureLoading(false);
  };

  const handleStopSearching = () => {
    setCancelLoading(true);
    setSearching(false);
    setInfos(null);
  };

  const handleVerifyRequiredInputs = () => {
    if (structureFilters.idMarca && structureFilters.idRepresentante) {
      setLocalStorageActions({
        nomeTela: "Manutenção de Informações Complementares",
        idMenu: "131",
        acao: "Clicou para Pesquisar",
      });
      return fetchList({
        page: 1,
        sizePerPage: currentSizePerPage,
      });
    } else {
      if (!structureFilters.idMarca) {
        setHasNoBrand(true);
      }

      if (!structureFilters.idRepresentante) {
        setHasNoRepresentative(true);
      }
    }
  };

  useEffect(() => {
    if (errorInfo !== "" && !cancelLoading) {
      snackbar.showMessage(errorInfo, {
        severity: "error",
      });
    }
  }, [cancelLoading, errorInfo]);

  useEffect(() => {
    if (structureFilters?.idMarca) {
      setHasNoBrand(false);
    }
    if (structureFilters?.idRepresentante) {
      setHasNoRepresentative(false);
    }

    if (
      structureFilters?.idMarca === null &&
      structureFilters?.idCanal === "" &&
      structureFilters?.idRepresentante === "" &&
      structureFilters?.idGrupoEconomico === ""
    ) {
      setClearFilters(false);
    }
  }, [structureFilters]);

  useEffect(() => {
    if (
      structureFilters.idMarca &&
      structureFilters.idRepresentante &&
      !firstSearch
    ) {
      setSearching(true);
      setTimeout(() => {
        setFirstSearch(true);
        fetchList({
          page: 1,
          sizePerPage: currentSizePerPage,
        });
      }, 1000);
    }
  }, [structureFilters.idMarca, structureFilters.idRepresentante]);

  useEffect(() => {
    if (hasUpdated) {
      fetchList({
        page: 1,
        sizePerPage: currentSizePerPage,
      });
    }
  }, [hasUpdated]);

  return (
    <>
      <HeaderClientes
        section="Manutenção de Informações Complementares de Loja"
        subsection="Gerenciamento"
      />
      <Container style={{ marginTop: "-4.5rem" }} maxWidth={false}>
        <Grid container>
          <Grid item xs={12}>
            <Card classes={{ root: classes.cardRoot }}>
              <CardHeader
                className={classes.cardHeader}
                title="Filtros"
                titleTypographyProps={{
                  component: Box,
                  marginBottom: "0!important",
                  variant: "h3",
                }}
              ></CardHeader>
              <CardContent>
                <Grid container spacing={2}>
                  <StructureFields
                    clearFilters={clearFilters}
                    onStructureFieldsChange={setStructureFilters}
                    disableAll={searching}
                    onStructureFieldsLoad={setStructureLoading}
                    requiredFields={requiredFields}
                    hasNoBrand={hasNoBrand}
                    hasNoRepresentative={hasNoRepresentative}
                  />
                  <Grid item xs={12}>
                    <Divider style={{ margin: "0.5rem 0" }} />
                  </Grid>
                  <Grid item xs={isMobile ? 12 : isMobile900 && 6}>
                    <Button
                      style={
                        isMobile || isMobile900
                          ? { width: "100%", marginBottom: "0.75rem" }
                          : { marginBottom: "0rem" }
                      }
                      variant="contained"
                      onClick={handleVerifyRequiredInputs}
                      disabled={searching || structureLoading.length > 0}
                    >
                      {searching ? "Pesquisando..." : "Pesquisar"}
                    </Button>
                  </Grid>
                  <Grid
                    item
                    xs={isMobile ? 12 : isMobile900 && 6}
                    style={{ display: "flex", alignItems: "center" }}
                  >
                    <ClearButton
                      disable={searching || structureLoading.length > 0}
                      onClick={() => handleClearFilters()}
                      fullWidth={true}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
            <LoadingData
              searching={searching}
              onClick={() => handleStopSearching()}
              classes={classes.root}
              label="Buscando dados..."
            />
          </Grid>
          {!searching && infos ? (
            <TableInfosComplementares
              dataTable={infos}
              totalRegistros={totalRegisters}
              currentPage={currentPage}
              currentSizePerPage={currentSizePerPage}
              search={fetchList}
              setSelectedStore={setSelectedStore}
            />
          ) : null}
        </Grid>
      </Container>
      {Boolean(selectedStore) && (
        <DialogManutencaoInfoComplementar
          open={Boolean(selectedStore)}
          handleClose={(loja) => {
            setSelectedStore(null);
            if (loja?.IdLoja > 0) {
              updateList(selectedStore, loja);
            }
            setSelectedStore(null);
          }}
          selectedStore={selectedStore}
        />
      )}
    </>
  );
}

ManutencaoInformacoesComplementares.propTypes = {};

export default ManutencaoInformacoesComplementares;
